import { DraftOnly } from "@components/project/DraftModeRequired.component";
import { useEntitlements } from "@frontegg/react";
import { PipelineNodeORM } from "@models/pipelineNode";
import { PageContentHeader } from "@pages/PageStructure.component";
import { getPromptAnswer, requireConfirmation } from "@services/alert/alert.service";
import { summarizeNumber } from "@services/formatting.service";
import { getNodeTypeConfig } from "@services/modeling.service";
import { timeAgo } from "@services/time.service";
import { useIsInDraftMode, usePipelineNode, useTemplateName, useIsFavoriteNode, toggleFavoriteNode, invalidateEverything } from "@stores/data.store";
import { ReactNode, useMemo, useCallback } from "react";
import { Badge, Dropdown } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import dbtIcon from '@assets/images/dbt.png';
import FavoriteButton from "@components/button/FavoriteButton.component";
import useGlobalState from "@stores/global.state";
import toast from "@services/toast.service";
interface Props {
    pipelineNodeId: string;
    children?: ReactNode;
}

const PipelineNodeSubnav = (props: Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const pipelineNode = usePipelineNode(props.pipelineNodeId);
    const inDraftMode = useIsInDraftMode();
    const isFavorite = useIsFavoriteNode(props.pipelineNodeId);

    const handleFavoriteClick = useCallback(async () => {
        await toggleFavoriteNode(props.pipelineNodeId);
    }, [props.pipelineNodeId]);

    const activePage = useMemo(() => {
        let path = location.pathname;

        if (path.endsWith('/')) {
            path = path.substring(0, path.length - 1);
        }
        
        if (path.endsWith('/config')) {
            return 'config';
        } else if (path.endsWith('/files')) {
            return 'files';
        } else if (path.indexOf('/relationships') >= 0) {
            return 'relationships';
        } else if (path.endsWith('/insights')) {
            return 'insights';
        } else if (path.endsWith('/filters')) {
            return 'filters';
        } else if (path.indexOf('/visualizations') >= 0) {
            return 'visualizations';
        }

        return 'data';
    }, [location]);

    const nodeConfig = useMemo(() => {
        if (!pipelineNode.data) {
            return undefined;
        }
        return getNodeTypeConfig(pipelineNode.data);
    }, [pipelineNode.dataUpdatedAt]);

    const nodeTypeConfig = useMemo(() => {
        if (!pipelineNode.data) {
            return undefined;
        }

        return getNodeTypeConfig(pipelineNode.data);
    }, [pipelineNode.dataUpdatedAt]);
    
    const currentNodeType = pipelineNode.data?.node_type;

    const copyNode = useCallback(async () => {
        const newLabel = await getPromptAnswer('Enter new name for the copied node', 'Copy Node', false, `${pipelineNode.data?.label} copy`);
        if (!newLabel) {
            return;
        }
        const newNode = await PipelineNodeORM.copyPipelineNode(props.pipelineNodeId, newLabel);
        invalidateEverything();
        navigate(`/node/${newNode.id}/config`);
    }, [props.pipelineNodeId, pipelineNode.dataUpdatedAt]);

    const { setPageDirty } = useGlobalState();

    const deleteNode = useCallback(async () => {
        const confirmed = await requireConfirmation(`Are you sure you want to delete this node? Type out the full node name ("${pipelineNode.data?.label}") to continue.`, 'Delete Node', 'Delete', 'Cancel', pipelineNode.data?.label);
        if (confirmed) {
            setPageDirty(false);
            await PipelineNodeORM.deleteById(props.pipelineNodeId);
            navigate('/');
            invalidateEverything();
            toast('success', 'Node deleted', 'Node deleted successfully.');
        }
    }, [props.pipelineNodeId, pipelineNode.dataUpdatedAt]);

    
    
   
    
    const badge = useMemo(() => {
        if (!pipelineNode.data || !nodeTypeConfig) {
            return <></>
        }

        const bg = nodeTypeConfig.group.color;
        const icon = pipelineNode.data.flat_file ? 'mdi mdi-file-document' : nodeTypeConfig.nodeType.icon;
        const label = pipelineNode.data.flat_file ? 'Flat File' : nodeTypeConfig.nodeType.label;


        return (
            <Badge
                pill
                bg={bg}
            ><i className={icon}></i> {label}
            </Badge>
        );
    }, [pipelineNode.data, nodeTypeConfig]);

    const templateName = useTemplateName(pipelineNode.data?.managed_by_template_id || '') || undefined;

    if (!pipelineNode.data || !nodeConfig) {
        return <></>;   
    }

    return <PageContentHeader>
            <div className="d-flex center-vertically" style={{height: '100%'}}>
                <FavoriteButton
                    isFavorite={isFavorite}
                    onToggle={handleFavoriteClick}
                    className="me-2"
                />
                <div className="flex-1 overflow-ellipsis">
                    
                    <h1 className="mb-0 overflow-ellipsis" style={{ maxWidth: '40vw' }} title={pipelineNode.data?.label}>
                        {pipelineNode.data?.label}
                    </h1>
                    <div className="font-poppins text-muted font-13">
                    {pipelineNode.data?.source_id && <>
                        <span role="button" onClick={() => navigate(`/source/${pipelineNode.data?.source_id}`)}>{badge}</span>
                    </>}
                    {!pipelineNode.data?.source_id && <>
                        <span>{badge}</span>
                    </>}
                        <span className="me-3">&nbsp;</span>
                        <span>
                            <i className="mdi mdi-file-multiple"></i> Records: {pipelineNode.data.total_records ? summarizeNumber(pipelineNode.data.total_records) : '0'}
                        </span>
                        <span className="me-3">&nbsp;</span>
                        <span>
                            <i className="mdi mdi-clock"></i> Last Updated: {pipelineNode.data.last_build_completed ? timeAgo(pipelineNode.data.last_build_completed, 'mini-minute-now') : 'never'}
                        </span>
                        <span className="me-3">&nbsp;</span>
                        <span title={templateName}>
                            {pipelineNode.data.managed_by_template_id && (
                                <i className="mdi mdi-image-frame"></i>
                            )}
                        </span>
                    </div>
                </div>
                <div className="me-1">
                    <Link to={`/node/${props.pipelineNodeId}`} className={`btn btn-light ${activePage == 'data' ? 'active' : ''}`}>
                        <i className="mdi mdi-table"></i> View Data
                    </Link>
                </div>
                {pipelineNode.data?.flat_file && <>
                    <div className="me-1">
                        <Link to={`/node/${props.pipelineNodeId}/files`} className={`btn btn-light ${activePage == 'files' ? 'active' : ''}`}>
                            <i className="mdi mdi-file-multiple"></i> Files
                        </Link>
                    </div>
                </>}
               
                <div className="me-3">
                    <Link to={`/node/${props.pipelineNodeId}/config`} className={`btn btn-light ${activePage == 'config' ? 'active' : ''}`}>
                        <i className="mdi mdi-cog"></i> Configure
                    </Link>
                </div>
                <div>
                    {props.children}
                </div>
                <div className="ms-1">
                    <Dropdown>
                        <Dropdown.Toggle variant="light" className="nocaret ps-1 pe-1">
                            <i className="mdi mdi-dots-vertical"></i>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Item onClick={copyNode}>
                                <i className="mdi mdi-content-copy"></i> Copy Node
                            </Dropdown.Item>
                            <Dropdown.Item onClick={deleteNode}>
                                <i className="mdi mdi-delete"></i> Delete Node
                            </Dropdown.Item>
                            
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                
            </div>
        
        
    </PageContentHeader>;
}

export default PipelineNodeSubnav;
